'use client';

import {
  createContext,
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  use,
  useState,
} from 'react';
import { DefaultError, useQuery } from '@tanstack/react-query';

import { useMenuService } from '@/data';
import { useSelectedStore } from '@/hooks';
import { TreezSpecials } from '@/types';

interface Specials {
  error: DefaultError | null;
  isError: boolean;
  isLoading: boolean;
  specials: TreezSpecials[];
  removeBUYProductBundleId?: string | null;
  setRemoveBUYProductBundleId: Dispatch<
    SetStateAction<string | null | undefined>
  >;
}

const SpecialsContext = createContext<Specials>({
  error: null,
  isError: false,
  isLoading: false,
  specials: [],
  removeBUYProductBundleId: null,
  setRemoveBUYProductBundleId: () => {},
});
SpecialsContext.displayName = 'SpecialsContext';

const SpecialsProvider: FC<PropsWithChildren> = ({ children }) => {
  const { store } = useSelectedStore();
  const { getSpecials } = useMenuService();
  const [removeBUYProductBundleId, setRemoveBUYProductBundleId] =
    useState(null);

  const { data, isLoading, error, isError } = useQuery({
    queryKey: ['specials', store?.shortName],
    queryFn: () => getSpecials(),
  });

  return (
    <SpecialsContext
      value={{
        isLoading,
        specials: data ?? [],
        error,
        isError,
        removeBUYProductBundleId,
        setRemoveBUYProductBundleId,
      }}
    >
      {children}
    </SpecialsContext>
  );
};

const useSpecialsContext = () => {
  const specialsContext = use(SpecialsContext);

  return specialsContext;
};

export { SpecialsProvider, useSpecialsContext };
